import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import InviteModal from "./InviteModal";
import { NAV, WATER } from "../lib/nav";

const LINK = "!text-ivory hover:!text-goldbright transition-colors [text-shadow:0_1px_5px_rgba(12,22,10,0.7)]";

export const jump = (to, after) => (e) => {
  document.activeElement?.blur();
  if (after) after();
  const [path, h] = to.split("#");
  if (window.location.pathname !== path) return;
  e.preventDefault();
  window.history.replaceState(null, "", to);
  const el = h && document.getElementById(h);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  else window.scrollTo({ top: 0, behavior: "smooth" });
};

export const NavLabel = ({ l }) => {
  const [main, soon] = l.split(" (coming soon)");
  return soon === undefined ? l : <>{main} <span className="text-[0.62em] opacity-50 tracking-wide">coming soon</span></>;
};

function DesktopItem({ n, active }) {
  const cls = `eyebrow ${n.small ? "!text-[0.52rem] opacity-80 pl-3 border-l border-white/30" : "!text-[0.68rem]"} !tracking-[0.2em] whitespace-nowrap !text-white [text-shadow:0_1px_3px_rgba(12,22,10,0.85)] hover:!text-goldbright transition-colors ${active === n.key ? "!text-goldbright" : ""}`;
  if (!n.items) return <Link to={n.to} onClick={jump(n.to)} data-testid={`nav-${n.key}`} className={cls}>{n.label}</Link>;
  return (
    <div className="relative group" data-testid={`nav-group-${n.key}`}>
      <Link to={n.to} onClick={jump(n.to)} data-testid={`nav-${n.key}`} className={`${cls} inline-flex items-center gap-1`}>
        {n.label}<ChevronDown size={14} strokeWidth={2.4} className="text-goldbright [filter:drop-shadow(0_1px_2px_rgba(12,22,10,0.9))] transition-transform group-hover:rotate-180" />
      </Link>
      <div className="absolute left-0 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-opacity duration-200">
        <div className="relative min-w-[200px] border border-white/25 py-4 shadow-xl overflow-hidden bg-cover bg-center" style={{ backgroundImage: `url(${WATER})` }}>
          <div className="absolute inset-0 bg-[#46603c]/[0.18]" />
          <div className="relative">
            <Link to={n.to} onClick={jump(n.to)} data-testid={`nav-${n.key}-top`} className="block mx-5 pb-2 mb-2 border-b border-gold/40 whitespace-nowrap hover:opacity-80"><p className="eyebrow !text-[0.52rem] !text-[#F6E3AE] font-bold [text-shadow:0_1px_3px_rgba(12,22,10,0.7)]">{n.eyebrow}</p><p className="eyebrow !text-[0.6rem] !text-ivory [text-shadow:0_1px_4px_rgba(12,22,10,0.6)]">{n.section}</p></Link>
            {n.items.filter((i) => !i[2]).map(([l, to]) => (
              <Link key={to} to={to} onClick={jump(to)} data-testid={`nav-${n.key}-${to.split("#")[1]}`}
                className="block px-5 py-2 text-sm font-medium text-white hover:text-goldbright transition-colors whitespace-nowrap [text-shadow:0_1px_4px_rgba(12,22,10,0.6)]"><NavLabel l={l} /></Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileMenu({ onClose }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="lg:hidden relative border-t border-gold/30 max-h-[75vh] overflow-auto bg-cover bg-center" style={{ backgroundImage: `url(${WATER})` }} data-testid="mobile-menu">
      {NAV.map((n) => (
        <div key={n.key} className="border-b border-white/10">
          {n.items ? (
            <>
              <button onClick={() => setOpen(open === n.key ? null : n.key)} data-testid={`mnav-${n.key}`}
                className="w-full flex items-center justify-between px-6 py-4 eyebrow !text-[0.66rem] !text-ivory">
                {n.label}<ChevronDown size={14} className={`transition-transform ${open === n.key ? "rotate-180" : ""}`} />
              </button>
              {open === n.key && (
                <div className="pb-3">
                  <Link to={n.to} onClick={jump(n.to, onClose)} data-testid={`mnav-${n.key}-top`} className="block mx-9 pb-2 mb-1 border-b border-gold/40"><p className="eyebrow !text-[0.52rem] !text-[#F6E3AE] font-bold">{n.eyebrow}</p><p className="eyebrow !text-[0.58rem] !text-ivory">{n.section}</p></Link>
                  {n.items.filter((i) => !i[2]).map(([l, to]) => (
                    <Link key={to} to={to} onClick={jump(to, onClose)} data-testid={`mnav-${n.key}-${to.split("#")[1]}`}
                      className="block px-9 py-2.5 text-sm font-medium text-white hover:text-goldbright [text-shadow:0_1px_4px_rgba(12,22,10,0.6)]"><NavLabel l={l} /></Link>
                  ))}
                </div>
              )}
            </>
          ) : (
            <Link to={n.to} onClick={jump(n.to, onClose)} data-testid={`mnav-${n.key}`} className="block px-6 py-4 eyebrow !text-[0.66rem] !text-ivory">{n.label}</Link>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Header({ active }) {
  const nav = useNavigate();
  const { pathname, hash } = useLocation();
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [invite, setInvite] = useState(false);
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setMobile(false); }, [pathname, hash]);
  return (
    <header className="fixed top-0 inset-x-0 z-40 border-b border-gold/40 text-ivory">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center water-drift" style={{ backgroundImage: `url(${WATER})` }} />
        <div className={`absolute inset-0 transition-colors duration-500 ${scrolled ? "bg-[#37502f]/72 backdrop-blur-[2px]" : "bg-[#46603c]/48"}`} />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 h-24 flex items-center justify-between gap-4">
        <Link to="/" data-testid="house-mark" className="flex items-center shrink-0">
          <img src="/logo-white.png" alt="LUXEWELL" className="no-grade h-[40px] w-auto object-contain" />
        </Link>
        <nav className="hidden lg:flex items-center gap-5 xl:gap-7 ml-auto mr-2">
          {NAV.map((n) => <DesktopItem key={n.key} n={n} active={active} />)}
        </nav>
        <div className="flex items-center gap-3 sm:gap-4">
          {user ? (
            <>
              <span className="hidden sm:inline eyebrow !text-[0.6rem] opacity-70">{user.name?.split(" ")[0]}</span>
              <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }}
                className={`eyebrow !text-[0.62rem] ${LINK}`}>Sign Out</button>
            </>
          ) : (
            <button data-testid="enter-house-btn" onClick={() => setInvite(true)} className="btn-gold eyebrow !text-[0.58rem] sm:!text-[0.62rem] px-3 sm:px-5 py-2.5 transition-colors whitespace-nowrap outline outline-1 outline-white/90 -outline-offset-[5px]">Enter the House</button>
          )}
          <button onClick={() => setMobile((v) => !v)} data-testid="mobile-menu-toggle" aria-label="Menu" className="lg:hidden text-ivory p-1">
            {mobile ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      {mobile && <MobileMenu onClose={() => setMobile(false)} />}
      <InviteModal open={invite} onClose={() => setInvite(false)} />
    </header>
  );
}
